<template>
  <div class="brand-form-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-trademark me-2 text-primary"></i>
            <strong>{{ obj.id ? 'Edit' : 'Add' }} Brand</strong>
          </h4>
          <small class="text-muted">{{ obj.id ? 'Update existing brand and manage models' : 'Create a new brand record' }}</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading brand data...</p>
      </div>
    </div>

    <!-- Brand Form -->
    <CRow v-else>
      <CCol sm="12">
        <CCard class="form-card">
          <CCardHeader class="form-header">
            <div class="d-flex align-items-center">
              <i class="fas fa-car me-2 text-primary"></i>
              <strong>Brand Information</strong>
            </div>
          </CCardHeader>
          <CCardBody>
            <CForm>
              <!-- Brand Name -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Name</strong>
                  <span class="text-danger ms-1">*</span>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="$v.obj.name.$model"
                    :isValid="checkIfValid('name')"
                    :readonly="isReadOnly"
                    invalidFeedback="Brand name is required (minimum 2 characters)"
                    placeholder="Enter brand name (e.g., Toyota, Honda)"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-info-circle me-1"></i>
                    Official name of the vehicle brand
                  </small>
                </CCol>
              </CRow>

              <!-- Models Section (only for edit mode) -->
              <div v-if="obj.id">
                <hr class="my-4" />

                <!-- Models Header -->
                <CRow class="mb-3">
                  <CCol>
                    <h6 class="section-header">
                      <i class="fas fa-list me-2 text-primary"></i>
                      <strong>Models</strong>
                      <CBadge color="info" class="ms-2">{{ obj.models ? obj.models.length : 0 }} Models</CBadge>
                    </h6>
                  </CCol>
                </CRow>

                <!-- Models Table -->
                <CRow class="mb-3" v-if="obj.models && obj.models.length > 0">
                  <CCol sm="12">
                    <div class="models-table-wrapper">
                      <CDataTable
                        :items="obj.models"
                        :fields="modelFields"
                        hover
                        border
                        striped
                      >
                        <template #name="{ item }">
                          <td>
                            <i class="fas fa-car-side me-2 text-muted"></i>
                            <strong>{{ item.name }}</strong>
                          </td>
                        </template>
                        <template #show_details="{ item }">
                          <td class="py-2 text-center">
                            <CButton
                              v-if="!isReadOnly"
                              color="danger"
                              variant="outline"
                              size="sm"
                              @click="removeModel(item)"
                            >
                              <i class="fas fa-trash-alt me-1"></i>
                              Remove
                            </CButton>
                            <span v-else class="text-muted">-</span>
                          </td>
                        </template>
                      </CDataTable>
                    </div>
                  </CCol>
                </CRow>

                <!-- Empty Models State -->
                <CRow class="mb-3" v-else>
                  <CCol sm="12">
                    <div class="empty-models">
                      <i class="fas fa-car-side fa-3x text-muted mb-3"></i>
                      <p class="text-muted">No models added yet</p>
                      <small class="text-muted">Add your first model using the form below</small>
                    </div>
                  </CCol>
                </CRow>

                <!-- Add Model Card -->
                <CRow class="mb-3">
                  <CCol sm="12">
                    <CCard class="add-model-card">
                      <CCardHeader class="add-model-header">
                        <i class="fas fa-plus-circle me-2 text-success"></i>
                        <strong>Add New Model</strong>
                      </CCardHeader>
                      <CCardBody>
                        <CRow class="align-items-end">
                          <CCol sm="9">
                            <CRow>
                              <CCol tag="label" sm="3" class="col-form-label">
                                <strong>Model Name</strong>
                              </CCol>
                              <CCol sm="9">
                                <CInput
                                  v-model="newModel.name"
                                  :readonly="isReadOnly"
                                  placeholder="Enter model name (e.g., Camry, Accord)"
                                />
                                <small class="form-text text-muted">
                                  <i class="fas fa-info-circle me-1"></i>
                                  Name of the vehicle model for this brand
                                </small>
                              </CCol>
                            </CRow>
                          </CCol>
                          <CCol sm="3">
                            <CButton
                              color="success"
                              block
                              :disabled="isReadOnly || !newModel.name || addingModel"
                              @click="addModel()"
                            >
                              <CSpinner v-if="addingModel" size="sm" class="me-2" />
                              <i v-else class="fas fa-plus me-2"></i>
                              {{ addingModel ? 'Adding...' : 'Add Model' }}
                            </CButton>
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              </div>

              <!-- Info Message for New Brand -->
              <CRow v-else class="mb-3">
                <CCol sm="12">
                  <CAlert color="info" :show="true">
                    <i class="fas fa-info-circle me-2"></i>
                    <strong>Note:</strong> You can add models after creating the brand.
                  </CAlert>
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>

          <!-- Form Footer -->
          <CCardFooter class="form-footer">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <CButton
                  v-if="canSubmit"
                  color="primary"
                  :disabled="!isValid || submitting"
                  @click="onSubmit"
                >
                  <CSpinner v-if="submitting" size="sm" class="me-2" />
                  <i v-else class="fas fa-check-circle me-2"></i>
                  {{ submitting ? 'Saving...' : 'Submit' }}
                </CButton>
                <CButton
                  color="secondary"
                  :class="canSubmit ? 'ms-2' : ''"
                  @click="cancel"
                  :disabled="submitting"
                >
                  <i class="fas fa-times me-2"></i>
                  {{ canSubmit ? 'Cancel' : 'Back' }}
                </CButton>
              </div>
              <div v-if="!isValid && canSubmit" class="text-danger">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <small>Please fill in all required fields</small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import BrandApi from "../../lib/brandApi";
import { required, minLength } from "vuelidate/lib/validators";

const modelFields = [
  {
    key: "name",
    label: "Model Name",
    _style: "min-width: 200px;"
  },
  {
    key: "show_details",
    label: "Actions",
    _style: "width: 120px; text-align: center;",
    sorter: false,
    filter: false,
  },
];

export default {
  name: "Brand",
  data: () => {
    return {
      newModel: {
        name: "",
      },
      modelFields,
      infoList: [],
      api: new BrandApi(),
      obj: {
        name: "",
      },
      submitted: false,
      submitting: false,
      loading: false,
      addingModel: false,
    };
  },
  mounted() {
    // Check view permission first
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }

    // If creating new, check CanAdd
    if (!this.$route.params.id && !this.canAdd) {
      this.$router.push('/admins/BrandList');
      this.toast('Access Denied', 'You do not have permission to add brands', 'danger');
      return;
    }

    this.obj = this.getEmptyObj();
    this.resetObj();
  },

  validations: {
    obj: {
      name: {
        required,
        minLength: minLength(2),
      },
    },
  },

  computed: {
    formString() {
      return JSON.stringify(this.obj, null, 4);
    },
    isValid() {
      return !this.$v.obj.$invalid;
    },
    isDirty() {
      return this.$v.obj.$anyDirty;
    },
    canView() {
      return this.$auth.hasSubModulePermission('BRANDS', 'CanView');
    },
    canAdd() {
      return this.$auth.hasSubModulePermission('BRANDS', 'CanAdd');
    },
    canUpdate() {
      return this.$auth.hasSubModulePermission('BRANDS', 'CanUpdate');
    },
    isReadOnly() {
      // If editing existing brand, check CanUpdate
      // If creating new brand, check CanAdd
      if (this.obj.id) {
        return !this.canUpdate;
      } else {
        return !this.canAdd;
      }
    },
    canSubmit() {
      if (this.obj.id) {
        return this.canUpdate;
      } else {
        return this.canAdd;
      }
    }
  },

  methods: {
    checkIfValid(fieldName) {
      const field = this.$v.obj[fieldName];
      return !(field.$invalid || field.$model === "");
    },

    addModel() {
      this.addingModel = true;
      this.api
        .addModel(this.obj.id, this.newModel)
        .then((response) => {
          this.newModel.name = "";
          this.toast("Success", "Model added successfully", "success");
          this.resetObj();
          this.addingModel = false;
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
          this.addingModel = false;
        });
    },

    removeModel(item) {
      this.api
        .removeModel(item)
        .then((response) => {
          this.toast("Success", "Model removed successfully", "success");
          this.resetObj();
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onSubmit() {
      this.submitting = true;
      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.toast("Success", "Brand created successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: `/admins/Brand/${response.result.id}` });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then((response) => {
            this.toast("Success", "Brand updated successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: "/admins/BrandList" });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      }
    },

    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    resetObj() {
      this.loading = true;
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.loading = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.loading = false;
          });
      } else {
        this.obj = this.getEmptyObj();
        this.loading = false;
      }
    },

    getEmptyObj() {
      return {
        name: "",
        models: [],
      };
    },

    cancel() {
      this.$router.push({ path: "/admins/BrandList" });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.brand-form-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header h4 {
  font-size: 1.5rem;
  color: var(--cui-body-color, #212529);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form Card */
.form-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.form-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

.form-header strong {
  font-size: 1.1rem;
  color: var(--cui-body-color, #212529);
}

/* Form Body */
.form-card .card-body {
  padding: 2rem 1.5rem;
}

/* Form Labels */
.col-form-label {
  font-weight: 500;
  color: var(--cui-body-color, #495057);
}

.col-form-label .text-danger {
  font-size: 1.2em;
}

/* Form Help Text */
.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Section Header */
.section-header {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--cui-border-color, #e9ecef);
}

/* Models Table */
.models-table-wrapper {
  border: 1px solid var(--cui-border-color, #dee2e6);
  border-radius: 0.25rem;
  overflow: hidden;
}

/* Empty Models State */
.empty-models {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: var(--cui-body-bg, #f8f9fa);
  border: 2px dashed var(--cui-border-color, #dee2e6);
  border-radius: 0.5rem;
  padding: 2rem;
}

.empty-models i {
  opacity: 0.5;
}

/* Add Model Card */
.add-model-card {
  border: 1px solid var(--cui-border-color, #dee2e6);
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.05);
}

.add-model-header {
  background: linear-gradient(135deg, rgba(46, 184, 92, 0.05) 0%, rgba(46, 184, 92, 0.02) 100%);
  border-bottom: 1px solid var(--cui-border-color, #dee2e6);
  padding: 0.75rem 1rem;
}

.add-model-header strong {
  color: #2eb85c;
}

.add-model-card .card-body {
  padding: 1.25rem;
}

/* Divider */
hr {
  border-top: 1px solid var(--cui-border-color, #dee2e6);
}

/* Form Footer */
.form-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .brand-form-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }

  .form-card .card-body {
    padding: 1.5rem 1rem;
  }

  .col-form-label {
    margin-bottom: 0.5rem;
  }

  .add-model-card .card-body {
    padding: 1rem;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .form-footer,
  .add-model-card {
    display: none !important;
  }

  .form-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }
}
</style>
